/**
 * Animated Hand Cursor für Tutorial
 * Zeigt animierte Click-Geste an der richtigen Position
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface TutorialHandCursorProps {
  targetRect: DOMRect | null;
  action: 'click' | 'drag' | 'none';
}

export function TutorialHandCursor({ targetRect, action }: TutorialHandCursorProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Kurze Verzögerung bevor Hand erscheint
    const timer = setTimeout(() => setShow(true), 500);
    return () => clearTimeout(timer);
  }, [targetRect]);

  if (!targetRect || action === 'none' || !show) {
    return null;
  }

  const centerX = targetRect.left + targetRect.width / 2;
  const centerY = targetRect.top + targetRect.height / 2;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed pointer-events-none z-[10003]"
        style={{
          left: centerX,
          top: centerY,
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
      >
        {action === 'click' && <ClickAnimation />}
        {action === 'drag' && <DragAnimation />}
      </motion.div>
    </AnimatePresence>
  );
}

function ClickAnimation() {
  return (
    <motion.div
      className="relative"
      animate={{
        y: [0, -5, 0],
      }}
      transition={{
        duration: 1.2,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      {/* Hand Icon */}
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
        }}
      >
        <motion.path
          d="M9 11V6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6V11M9 11V4C9 3.44772 9.44772 3 10 3C10.5523 3 11 3.44772 11 4V11M9 11V4C9 3.44772 9.44772 3 10 3C10.5523 3 11 3.44772 11 4V11"
          stroke="#FFD700"
          strokeWidth="2"
          strokeLinecap="round"
          fill="#FFF"
        />
        <motion.path
          d="M11 11V7C11 6.44772 11.4477 6 12 6C12.5523 6 13 6.44772 13 7V11M13 11V8C13 7.44772 13.4477 7 14 7C14.5523 7 15 7.44772 15 8V11M15 11V9C15 8.44772 15.4477 8 16 8C16.5523 8 17 8.44772 17 9V13C17 16.3137 14.3137 19 11 19H10C6.68629 19 4 16.3137 4 13V11"
          stroke="#FFD700"
          strokeWidth="2"
          strokeLinecap="round"
          fill="#FFF"
        />
      </svg>

      {/* Click Ripple Effect */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        initial={{ scale: 0.8, opacity: 0.8 }}
        animate={{ scale: 1.8, opacity: 0 }}
        transition={{
          duration: 1.2,
          repeat: Infinity,
          ease: "easeOut",
        }}
      >
        <div
          className="w-12 h-12 rounded-full border-4"
          style={{ borderColor: '#FFD700' }}
        />
      </motion.div>
    </motion.div>
  );
}

function DragAnimation() {
  return (
    <motion.div
      className="relative"
      animate={{
        x: [-10, 10, -10],
        y: [-10, 10, -10],
      }}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      {/* Hand Icon */}
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
        }}
      >
        <path
          d="M9 11V6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6V11M9 11V4C9 3.44772 9.44772 3 10 3C10.5523 3 11 3.44772 11 4V11M9 11V4C9 3.44772 9.44772 3 10 3C10.5523 3 11 3.44772 11 4V11"
          stroke="#FFD700"
          strokeWidth="2"
          strokeLinecap="round"
          fill="#FFF"
        />
        <path
          d="M11 11V7C11 6.44772 11.4477 6 12 6C12.5523 6 13 6.44772 13 7V11M13 11V8C13 7.44772 13.4477 7 14 7C14.5523 7 15 7.44772 15 8V11M15 11V9C15 8.44772 15.4477 8 16 8C16.5523 8 17 8.44772 17 9V13C17 16.3137 14.3137 19 11 19H10C6.68629 19 4 16.3137 4 13V11"
          stroke="#FFD700"
          strokeWidth="2"
          strokeLinecap="round"
          fill="#FFF"
        />
      </svg>

      {/* Drag Trail */}
      <motion.div
        className="absolute top-1/2 left-1/2 w-1 h-1 bg-yellow-400 rounded-full"
        style={{
          boxShadow: '0 0 10px rgba(255,215,0,0.6)',
        }}
      />
    </motion.div>
  );
}
